// Copyright (c) 2026 Finerty Data Ltd. All rights reserved.
//
// This file is part of proprietary software owned by Finerty Data Ltd.
// It is confidential and may not be copied, distributed, modified, or used,
// in whole or in part, without the prior written consent of Finerty Data Ltd.
//
// Author: Ahmed Hosny

import { useCallback, useEffect, useState } from 'react';
import { Badge, Button, DataTable, Panel, StatusBanner, useToast } from '@loa/ui-components';
import type { DataTableColumn, SelectOption } from '@loa/ui-components';
import { useAdvisor } from '../contexts/AdvisorContext';
import { brokerageConfigApi } from '../api/brokerageConfigApi';
import { quoteTermApi } from '../api/quoteTermApi';
import type { QuoteTerm } from '../api/quoteTermApi';
import { QuoteTermsEditorModal } from './QuoteTermsEditorModal';
import { QuoteTermOptionsModal } from './QuoteTermOptionsModal';

type Props = Readonly<{ providerId: string }>;

/**
 * "Terms & Quotes" card on the Provider Detail → Settings tab. Lists the
 * brokerage's rate cards for this provider. Adding / editing / deleting is
 * restricted to Brokerage Admins; other advisors see the cards read-only.
 * See docs/specs/quotes-engine-prd.md §7.2.
 */
export function QuoteTermsCard({ providerId }: Props) {
  const { advisor, isAdmin } = useAdvisor();
  const { showToast } = useToast();
  const brokerageId = advisor?.brokerageId ? String(advisor.brokerageId) : '';

  const [cards, setCards] = useState<QuoteTerm[]>([]);
  const [productOptions, setProductOptions] = useState<SelectOption[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [editorOpen, setEditorOpen] = useState(false);
  const [editing, setEditing] = useState<QuoteTerm | null>(null);
  const [viewing, setViewing] = useState<QuoteTerm | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      // Platform defaults (read-only) shown above the brokerage's own overrides.
      const [platform, own] = await Promise.all([
        quoteTermApi.listPlatform(providerId),
        quoteTermApi.list(providerId),
      ]);
      setCards([...platform, ...own]);
      if (brokerageId) {
        const products = await brokerageConfigApi.getEnabledPolicyTypes(brokerageId, providerId);
        setProductOptions(
          products.content.map((bpt) => ({ value: bpt.policyType.id, text: bpt.policyType.name })),
        );
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load terms');
    } finally {
      setLoading(false);
    }
  }, [providerId, brokerageId]);

  useEffect(() => { void load(); }, [load]);

  const openAdd = () => { setEditing(null); setEditorOpen(true); };
  const openEdit = async (termId: string) => {
    try {
      setEditing(await quoteTermApi.get(termId));
      setEditorOpen(true);
    } catch {
      showToast('Failed to load rate card', 'error');
    }
  };

  const remove = async (card: QuoteTerm) => {
    if (!card.id) return;
    if (!globalThis.confirm(`Delete the ${card.productName ?? 'rate card'} terms?`)) return;
    try {
      await quoteTermApi.remove(card.id);
      showToast('Terms deleted', 'success');
      void load();
    } catch {
      showToast('Failed to delete terms', 'error');
    }
  };

  const isPlatform = (card: QuoteTerm) => card.scope === 'PLATFORM';

  const columns: DataTableColumn<QuoteTerm>[] = [
    { key: 'product', header: 'Product', render: (card) => card.productName ?? '—' },
    {
      key: 'source',
      header: 'Source',
      width: '120px',
      render: (card) =>
        isPlatform(card) ? (
          <Badge color="blue" variant="outlined" title="Maintained platform default — read-only">
            Platform
          </Badge>
        ) : (
          <Badge color="purple" variant="filled">Yours</Badge>
        ),
    },
    { key: 'category', header: 'Category', render: (card) => card.category ?? '—' },
    { key: 'options', header: 'Options', width: '96px', render: (card) => String(card.options?.length ?? 0) },
    { key: 'currency', header: 'Currency', width: '120px', render: (card) => card.currencyCode },
    {
      key: 'status',
      header: 'Status',
      width: '120px',
      render: (card) => (
        <Badge color={card.isActive ? 'green' : 'grey'} variant={card.isActive ? 'filled' : 'outlined'}>
          {card.isActive ? 'Active' : 'Inactive'}
        </Badge>
      ),
    },
    {
      key: 'actions',
      header: '',
      width: '220px',
      render: (card: QuoteTerm) => (
        <div className="flex items-center gap-2">
          <Button variant="outlined" size="sm" onClick={() => setViewing(card)}>View</Button>
          {isAdmin && !isPlatform(card) && (
            <>
              <Button variant="outlined" size="sm" onClick={() => openEdit(card.id!)}>Edit</Button>
              <Button variant="outlined" size="sm" colour="red" onClick={() => remove(card)}>Delete</Button>
            </>
          )}
        </div>
      ),
    },
  ];

  return (
    <Panel
      title="Terms & Quotes"
      description="Provider allocation rates & charges used to generate quotes for this brokerage."
      headerActions={
        isAdmin ? (
          <Button
            variant="outlined" size="sm" onClick={openAdd}
            disabled={productOptions.length === 0} data-testid="add-terms"
          >
            Add Terms &amp; Quotes
          </Button>
        ) : undefined
      }
      data-testid="terms-and-quotes-card"
    >
      {error && <StatusBanner variant="error" message={error} visible position="inline" dismissible={false} />}

      <DataTable<QuoteTerm>
        columns={columns}
        data={cards}
        rowKey={(card) => card.id ?? ''}
        loading={loading}
        emptyState="No terms configured for this provider yet."
      />

      {editorOpen && (
        <QuoteTermsEditorModal
          open
          providerId={providerId}
          productOptions={productOptions}
          existing={editing}
          onClose={() => setEditorOpen(false)}
          onSaved={() => {
            setEditorOpen(false);
            showToast('Terms saved', 'success');
            void load();
          }}
        />
      )}

      {viewing && (
        <QuoteTermOptionsModal open card={viewing} onClose={() => setViewing(null)} />
      )}
    </Panel>
  );
}
